/**
 * Custom Product Grid (homepage Best Sellers / New Arrivals): busy and error states on Add to Cart.
 *
 * With an empty cart the drawer only opens once /cart/add.js and the cart refresh have
 * both come back (cart-drawer.js won't slide in an empty drawer), which is ~1s+ in which
 * the card button showed nothing — it read as "the click didn't work". The PDP button has
 * add-to-cart-component for feedback; these cards don't, so this covers the gap.
 *
 * product-form-component dispatches CartLinesUpdateEvent synchronously on submit and
 * settles event.promise when the add (or its error) is done, so that's the whole window.
 * A failed add also dispatches CartErrorEvent first; the cards have no error element like
 * the PDP does, so the message goes on the button for a few seconds instead.
 */
import { StandardEvents } from '@shopify/events';

const BUTTON_SEL = '[data-cps-atc]';
const ERROR_DISPLAY_MS = 3000;

/** @type {WeakMap<Element, string>} */
const lastError = new WeakMap();

/** @param {Event} event */
function buttonFor(event) {
  const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  return /** @type {HTMLButtonElement | null} */ (form?.querySelector(BUTTON_SEL) ?? null);
}

document.addEventListener(StandardEvents.cartError, (event) => {
  const button = buttonFor(event);
  if (button) lastError.set(button, String(/** @type {any} */ (event).error || ''));
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const button = buttonFor(event);
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  const label = button.textContent;
  lastError.delete(button);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = button.dataset.cpsAtcBusy || label;

  const reset = () => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = label;
  };

  const fail = () => {
    const message = lastError.get(button) || '';
    button.removeAttribute('aria-busy');
    button.textContent = /sold out/i.test(message)
      ? button.dataset.cpsAtcSoldOut || label
      : button.dataset.cpsAtcError || label;
    setTimeout(reset, ERROR_DISPLAY_MS);
  };

  const promise = /** @type {any} */ (event).promise;
  if (promise && typeof promise.then === 'function') {
    promise.then((/** @type {any} */ result) => (result?.detail?.didError ? fail() : reset()), fail);
  } else {
    reset();
  }
});
